import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AddIcon from '@mui/icons-material/Add'
import {
  Checkbox,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material'
import React, { FC, useMemo, useState, useEffect, useRef } from 'react'
import { YakurekiDetail } from '@/shared/types/yakurekiCore'
import {
  SoapSectionKeys,
  SoapContent,
  SoapContentItem,
} from '@/shared/types/yakurekiSoap'
import {
  PrescriptionGroup,
  DrugInfo,
} from '@/shared/types/yakurekiPrescription'
import { getSectionContentItems } from '@/apps/legacy/features/formatter/helpers/soapDataUtils'
import {
  performAllSectionsReplacement,
  performSingleItemReplacement,
} from '@/apps/legacy/features/formatter/helpers/soapReplacementHelpers'
import { soapLabels } from '@/shared/constants/const'
import { MedicationPlaceholderText } from '@/apps/legacy/components/molecules/MedicationPlaceholderText'
import { EditableSOAPItem } from '@/apps/legacy/components/molecules/EditableSOAPItem'
import EditIcon from '@mui/icons-material/Edit'
import {
  useUpdateSoapMutation,
  useSelectionFromYakureki,
  useToggleSelectionMutation,
  useToggleHeaderMutation,
} from '@/features/yakureki'
import { useToast } from '@/shared/contexts/ToastContext'
import { useMedicationUpdate } from '@/apps/legacy/shared/contexts/MedicationUpdateContext'
import { createMedicationRegex } from '@/shared/constants/medicationPatterns'
import {
  handleAsyncError,
  captureException,
} from '@/shared/utils/errorHandling'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

type Props = {
  soapKey: SoapSectionKeys
  copySection: (key: SoapSectionKeys) => void
  yakurekiDetail: YakurekiDetail | undefined
  loadYakurekiDetail: () => Promise<void>
  isEditMode?: boolean
  medications?: string[]
}

export const SoapCheckBoxes: FC<Props> = ({
  soapKey,
  copySection,
  yakurekiDetail,
  loadYakurekiDetail,
  isEditMode = false,
  medications = [],
}) => {
  // SOAPコンテンツアイテムを取得（新形式対応）
  const soapContentItems = useMemo(() => {
    if (!yakurekiDetail?.soaps) return []
    return getSectionContentItems(yakurekiDetail.soaps, soapKey)
  }, [yakurekiDetail?.soaps, soapKey])
  // コンポーネントのマウント状態を追跡するref
  const isMounted = useRef(true)

  // コンポーネントのマウント状態を管理
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // TanStack Query から選択状態を取得（ADR-055 対応）
  const { selection } = useSelectionFromYakureki(yakurekiDetail?.id)
  const toggleSelectionMutation = useToggleSelectionMutation(yakurekiDetail?.id)
  const toggleHeaderMutation = useToggleHeaderMutation(yakurekiDetail?.id)

  // 薬剤更新コンテキストを使用
  const { setUpdating } = useMedicationUpdate()

  // TanStack Query Mutation
  const updateSoapMutation = useUpdateSoapMutation()

  // 編集中のアイテムを管理
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // 新規項目追加の状態管理
  const [isAddingNew, setIsAddingNew] = useState(false)

  // 指定されたインデックスの選択状態を確認する補助関数（ADR-055 対応）
  const isSelected = (index: number): boolean => {
    return selection[soapKey]?.has(index) ?? false
  }

  // ヘッダーが選択されているか確認する補助関数
  // ADR-055: 全選択状態の場合にヘッダーも選択とみなす
  const isHeaderSelected = (): boolean => {
    const selectedCount = selection[soapKey]?.size ?? 0
    const totalCount = soapContentItems.length
    // 全選択状態 または アイテムがない場合
    return totalCount === 0 || selectedCount === totalCount
  }

  // 新規項目の追加処理
  const handleAddNewItem = async (newContent: string) => {
    if (!yakurekiDetail?.soaps || !yakurekiDetail.id) return
    if (!newContent.trim()) {
      setIsAddingNew(false)
      return
    }

    setIsSaving(true)
    try {
      // 新しいアイテムを作成 (is_by_ai は optional なので付与しない)
      const newItem: SoapContentItem = {
        content: newContent,
        is_selected: true, // 新規追加は自動選択
      }

      // 既存のアイテムに新規アイテムを追加
      const updatedSectionItems = [...soapContentItems, newItem]

      const updatedSoaps = {
        ...yakurekiDetail.soaps,
        [soapKey]: updatedSectionItems,
      }

      // Mutationで更新（キャッシュも自動invalidate）
      await updateSoapMutation.mutateAsync({
        yakurekiId: yakurekiDetail.id,
        soapData: updatedSoaps,
        editSource: 'manual_edit',
      })

      // データの再取得を待つ
      await loadYakurekiDetail()

      // コンポーネントがアンマウントされていないかチェック
      if (!isMounted.current) return

      setIsAddingNew(false)
      showToast('項目を追加しました', 'success', 1500)
    } catch (error) {
      if (!isMounted.current) return

      setIsSaving(false)
      setIsAddingNew(false)
      handleAsyncError(error, '追加に失敗しました', isMounted, showToast)
    } finally {
      if (isMounted.current) {
        setIsSaving(false)
      }
    }
  }

  // 編集の保存処理
  const handleSaveEdit = async (index: number, newContent: string) => {
    if (!yakurekiDetail?.soaps || !yakurekiDetail.id) return

    setIsSaving(true)
    try {
      // SOAPデータを更新
      const updatedSectionItems = [...soapContentItems]
      updatedSectionItems[index] = {
        ...updatedSectionItems[index],
        content: newContent,
      }

      const updatedSoaps = {
        ...yakurekiDetail.soaps,
        [soapKey]: updatedSectionItems,
      }

      // Mutationで更新（キャッシュも自動invalidate）
      await updateSoapMutation.mutateAsync({
        yakurekiId: yakurekiDetail.id,
        soapData: updatedSoaps,
        editSource: 'manual_edit',
      })

      // データの再取得を待つ
      await loadYakurekiDetail()

      // コンポーネントがアンマウントされていないかチェック
      if (!isMounted.current) return

      // loadYakurekiDetailの完了後に編集状態をリセット
      // これにより、新しいデータが反映されてから表示が切り替わる
      setEditingIndex(null)
      showToast('保存しました', 'success', 1500)
    } catch (error) {
      if (!isMounted.current) return

      // isSavingをfalseにしてから編集状態をリセット
      setIsSaving(false)
      setEditingIndex(null)
      handleAsyncError(error, '保存に失敗しました', isMounted, showToast)
    } finally {
      if (isMounted.current) {
        setIsSaving(false)
      }
    }
  }

  // インデックスを切り替える関数（ADR-055 対応）
  const toggleSelectionByIndex = (index: number) => {
    // TanStack Query Mutation で選択状態を更新
    toggleSelectionMutation.mutate({ key: soapKey, index })
  }

  const { showToast } = useToast()
  // パフォーマンス最適化: 選択統計情報をメモ化（ADR-055 対応）
  const selectionStats = useMemo(() => {
    const selectedIndices = selection[soapKey] || new Set<number>()
    const selectedCount = selectedIndices.size
    const totalCount = soapContentItems.length

    // ADR-055: ヘッダー選択は「全選択状態」または「空のセクション」を意味する
    const hasHeader = totalCount === 0 || selectedCount === totalCount
    const hasContentItems = selectedCount > 0

    return {
      hasHeader,
      hasContentItems,
      selectedCount,
      totalCount,
      areAllSelected: totalCount > 0 && selectedCount === totalCount,
      hasPartialSelection: selectedCount > 0 && selectedCount < totalCount,
      hasHeaderOnlyWithEmptyContent: hasHeader && totalCount === 0,
      isOnlyHeaderSelected: hasHeader && !hasContentItems,
      hasAnySelection: selectedCount > 0 || totalCount === 0,
    }
  }, [selection, soapKey, soapContentItems])

  const hasSelectedContent =
    selectionStats.hasContentItems ||
    selectionStats.hasHeaderOnlyWithEmptyContent

  const hasMediciationContent = (content: string, medInfo: string[]) => {
    const medPlaceholderRegex = createMedicationRegex(medInfo)
    return medPlaceholderRegex.test(content)
  }

  const replaceMedication = async (
    originalSoap: string,
    placeholder: string,
    replacement: string,
    clickedIndex: number,
    replaceAll?: boolean
  ) => {
    if (!yakurekiDetail) {
      return false
    }

    // 薬品更新フラグを設定
    setUpdating(true)

    try {
      if (!yakurekiDetail.soaps) {
        return false
      }

      const targetSoaps = yakurekiDetail.soaps[soapKey] || []

      // インデックスの検証
      if (
        clickedIndex < 0 ||
        clickedIndex >= targetSoaps.length ||
        targetSoaps[clickedIndex].content !== originalSoap
      ) {
        return false
      }

      // サーバー更新用のSOAPデータを準備
      // idフィールドを除外してsoapContentを取得
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...soapContent } = yakurekiDetail.soaps
      let updatedSoaps: SoapContent
      let allUpdatedIndicesBySection: {
        [key in SoapSectionKeys]?: number[]
      }

      if (replaceAll) {
        // 全セクションを横断して置換（ADR-055: Selection 型を使用）
        const result = performAllSectionsReplacement(
          soapContent,
          placeholder,
          replacement,
          selection
        )
        updatedSoaps = { ...soapContent, ...result.updatedSoaps }
        allUpdatedIndicesBySection = result.updatedIndicesBySection
      } else {
        // 単一置換の場合（現在のセクションのみ）
        // ADR-055: selection[soapKey] は Set<number>
        const selectedIndices = selection[soapKey] || new Set<number>()

        const result = performSingleItemReplacement(
          targetSoaps,
          placeholder,
          replacement,
          clickedIndex,
          selectedIndices
        )

        updatedSoaps = { ...soapContent }
        updatedSoaps[soapKey] = result.updatedItems
        allUpdatedIndicesBySection = {
          [soapKey]: result.updatedIndices,
        }

        // 他のセクションの選択状態を維持（ADR-055: Selection 型を使用）
        Object.keys(soapContent).forEach((key) => {
          const typedKey = key as SoapSectionKeys
          if (typedKey !== soapKey && soapContent[typedKey]) {
            const selectedIndices = selection[typedKey] || new Set<number>()
            const sectionItems = soapContent[typedKey]
            if (!sectionItems) return
            updatedSoaps[typedKey] = sectionItems.map((item, idx) => ({
              ...item,
              is_selected: selectedIndices.has(idx) ?? item.is_selected,
            }))
          }
        })
      }

      // Mutationで更新（キャッシュも自動invalidate）
      await updateSoapMutation.mutateAsync({
        yakurekiId: yakurekiDetail.id,
        soapData: updatedSoaps,
        editSource: 'manual_edit',
      })

      // loadYakurekiDetailで最新状態を取得
      // ADR-055: TanStack Query キャッシュが更新され、selection は自動で最新になる
      await loadYakurekiDetail()

      // ADR-055: 選択状態は is_selected フラグとして SOAP データに保存され、
      // TanStack Query キャッシュから自動的に派生するため、手動更新は不要
      // allUpdatedIndicesBySection は未使用だが、ログ用に残す
      void allUpdatedIndicesBySection

      return true
    } catch (error) {
      // エラーログを記録（改善：より詳細な情報を追加）
      captureException(error, {
        context: 'SoapCheckBoxes.replaceMedication',
        yakurekiDetailId: yakurekiDetail?.id,
        soapKey,
        clickedIndex,
        placeholder,
        replacement,
        replaceAll: replaceAll || false,
        updatedIndicesCount: 0, // エラー時はupdatedIndicesが存在しない可能性
        targetSoapsLength: 0, // エラー時にtargetSoapsが存在しない可能性
      })

      // ユーザーにエラーを通知
      showToast('薬品の更新に失敗しました。もう一度お試しください。', 'error')

      return false
    } finally {
      // 必ず更新フラグをリセット
      setUpdating(false)
    }
  }

  const renderSoapContent = (content: string, index: number) => {
    if (!yakurekiDetail) {
      return content
    }

    // 処方データから薬品名リストを抽出
    const medInfo: string[] = []
    if (yakurekiDetail.current_prescription_data) {
      yakurekiDetail.current_prescription_data.prescription_groups.forEach(
        (group: PrescriptionGroup) => {
          group.drugs.forEach((drug: DrugInfo) => {
            medInfo.push(drug.drug_name)
          })
        }
      )
    }

    if (medInfo.length === 0) {
      return content
    }

    // Check if the content has medication placeholders or actual medication names
    const hasMedContent = hasMediciationContent(content, medInfo)

    if (hasMedContent) {
      // 重要: インデックス情報を一緒に渡して、どの要素がクリックされたか特定できるようにする
      return (
        <MedicationPlaceholderText
          text={content}
          medInfo={medInfo}
          onReplace={(placeholder, replacement, replaceAll) =>
            replaceMedication(
              content,
              placeholder,
              replacement,
              index,
              replaceAll
            )
          }
        />
      )
    }

    return content
  }

  return (
    <Stack width="100%" spacing={0.25}>
      {soapContentItems.length === 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: 32,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Tooltip title="まとめて選択" placement="top" arrow>
                <Box
                  onClick={() => {
                    // ADR-055: 空のセクションの場合、ヘッダーは常に選択状態
                    // クリックしても何も変わらない（視覚的なフィードバックのみ）
                  }}
                  sx={{
                    backgroundColor: isHeaderSelected()
                      ? 'rgba(158, 158, 158, 0.08)'
                      : 'transparent',
                    border: isHeaderSelected()
                      ? '1px solid rgba(158, 158, 158, 0.08)'
                      : '1px solid rgba(158, 158, 158, 0.3)',
                    borderRadius: '4px',
                    flex: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(158, 158, 158, 0.08)',
                    },
                    transition: 'background-color 0.2s ease',
                    position: 'relative',
                  }}
                >
                  <Typography
                    sx={{
                      color: colorTokens.grey[800],
                      py: 0.5,
                      textAlign: 'center',
                      width: '48px',
                      ml: 0.5,
                    }}
                  >
                    {soapLabels[soapKey]}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title="コピー不可" placement="top" arrow>
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <IconButton
                    size="small"
                    onClick={() => {}}
                    sx={{
                      ml: 0.5,
                      color: 'rgba(0, 0, 0, 0.26)',
                      opacity: 0.6,
                      '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                    }}
                    disabled={true}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minHeight: 28,
              py: 0,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                ml: 1,
                mr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
            <Typography
              sx={{
                color: colorTokens.grey[500],
                fontStyle: 'italic',
              }}
            >
              記載内容がありません
            </Typography>
          </Box>
        </>
      )}
      {soapContentItems.length !== 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: 32,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Tooltip title="まとめて選択" placement="top" arrow>
                <Box
                  onClick={() => {
                    // ADR-055: TanStack Query Mutation で全選択/全解除
                    toggleHeaderMutation.mutate({ key: soapKey })
                  }}
                  sx={{
                    backgroundColor: selectionStats.areAllSelected
                      ? 'rgba(158, 158, 158, 0.08)'
                      : selectionStats.hasPartialSelection
                      ? 'rgba(158, 158, 158, 0.05)'
                      : 'transparent',
                    border: selectionStats.areAllSelected
                      ? '1px solid rgba(158, 158, 158, 0.08)'
                      : selectionStats.hasPartialSelection
                      ? '1px solid rgba(158, 158, 158, 0.05)'
                      : '1px solid rgba(158, 158, 158, 0.3)',
                    borderRadius: '4px',
                    flex: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(158, 158, 158, 0.08)',
                    },
                    transition: 'background-color 0.2s ease',
                    position: 'relative',
                  }}
                >
                  <Typography
                    sx={{
                      color: colorTokens.grey[800],
                      py: 0.5,
                      textAlign: 'center',
                      width: '48px',
                      ml: 0.5,
                    }}
                  >
                    {soapLabels[soapKey]}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  hasSelectedContent
                    ? `選択した${soapLabels[soapKey]}をコピー`
                    : '項目を選択してください'
                }
                placement="top"
                arrow
              >
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <IconButton
                    size="small"
                    onClick={() => hasSelectedContent && copySection(soapKey)}
                    sx={[
                      {
                        ml: 0.5,
                        '&.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      },
                      hasSelectedContent
                        ? {
                            color: colorTokens.blue[500],
                          }
                        : {
                            color: 'rgba(0, 0, 0, 0.26)',
                          },
                      hasSelectedContent
                        ? {
                            opacity: 1,
                          }
                        : {
                            opacity: 0.6,
                          },
                    ]}
                    disabled={!hasSelectedContent}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}
      {soapContentItems.map((soapItem, index) => {
        // インデックスベースの選択状態をチェック
        const isChecked = isSelected(index)

        const content = soapItem?.content || ''

        return (
          <Box
            key={`soap-item-${soapKey}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              position: 'relative',
              minHeight: 32,
              py: 0,
            }}
          >
            <Checkbox
              sx={{
                height: 24,
                pr: 1,
                ml: 1,
                '&.Mui-checked': {
                  color: colorTokens.blue[500],
                },
                '&:not(.Mui-checked)': {
                  color: colorTokens.grey[500],
                },
              }}
              size="small"
              color="primary"
              checked={isChecked}
              onChange={() => {
                // インデックスベースで選択状態を切り替え（ADR-055）
                toggleSelectionByIndex(index)
              }}
              value={`${soapKey}-${index}`} // インデックスベースで一意の値を使用
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flex: 1,
                pt: 0.5, // Align with checkbox
              }}
            >
              {isEditMode && editingIndex === index ? (
                // 編集モード且つこのアイテムが編集中
                <EditableSOAPItem
                  content={content}
                  medications={medications}
                  isEditMode={isEditMode}
                  isEditing={true}
                  onStartEdit={() => {}}
                  onSave={(newContent) => handleSaveEdit(index, newContent)}
                  onCancel={() => !isSaving && setEditingIndex(null)}
                  disabled={isSaving}
                />
              ) : (
                // 通常表示または編集モードで編集中でない
                <Box
                  sx={[
                    {
                      display: 'flex',
                      flex: 1,
                      alignItems: 'flex-start',
                    },
                    isEditMode && {
                      backgroundColor: 'rgba(25, 118, 210, 0.02)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                      },
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    },
                  ]}
                  onClick={() => {
                    if (isEditMode && editingIndex === null) {
                      setEditingIndex(index)
                    }
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      color: '#000',
                    }}
                  >
                    {renderSoapContent(content, index)}
                  </Typography>
                  {isEditMode && editingIndex === null && (
                    <Tooltip title="クリックして編集" placement="left">
                      <EditIcon
                        fontSize="small"
                        sx={{
                          ml: 1,
                          color: colorTokens.text.secondary,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )
      })}
      {/* 編集モード時の新規項目追加ボタン */}
      {isEditMode && !isAddingNew && editingIndex === null && (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              ml: 1,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
          <Box
            onClick={() => setIsAddingNew(true)}
            sx={{
              flex: 1,
              px: 1.5,
              py: 0.8,
              border: `1.5px dashed ${colorTokens.border.medium}`,
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(158, 158, 158, 0.04)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(158, 158, 158, 0.08)',
                borderColor: colorTokens.grey[500],
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <AddIcon
              sx={{ mr: 0.5, color: colorTokens.grey[500], fontSize: 18 }}
            />
            <Typography
              sx={{
                color: colorTokens.text.secondary,
              }}
            >
              新しい項目を追加
            </Typography>
          </Box>
        </Box>
      )}
      {/* 新規項目の入力フィールド */}
      {isAddingNew && (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            position: 'relative',
            minHeight: 32,
            py: 0,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              ml: 1,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ color: colorTokens.grey[500], fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, pt: 0.5 }}>
            <EditableSOAPItem
              content=""
              medications={medications}
              isEditMode={true}
              isEditing={true}
              onStartEdit={() => {}}
              onSave={handleAddNewItem}
              onCancel={() => {
                if (!isSaving) {
                  setIsAddingNew(false)
                }
              }}
              disabled={isSaving}
            />
          </Box>
        </Box>
      )}
    </Stack>
  )
}
